import { PartyPopper, X } from 'lucide-react';
import { toast } from 'sonner';
import { triggerCelebration } from '@/hooks/useConfetti';

// Module-level audio object that gets primed on user gesture
let primedAudio: HTMLAudioElement | null = null;
let isAudioPrimed = false;

// Get the celebration sound URL
function getCelebrationSoundUrl(): string {
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const baseUrl = isLocalhost
    ? 'http://localhost:3005'
    : 'https://podcast-manager-rose.vercel.app';
  return `${baseUrl}/celebration.mp3`;
}

// Prime the audio element - call this from a user gesture (like clicking Publish)
// This satisfies browser autoplay restrictions
export function primeCelebrationSound(): void {
  try {
    const soundUrl = getCelebrationSoundUrl();
    primedAudio = new Audio(soundUrl);
    primedAudio.volume = 0.7;
    // Load the audio so it's ready to play
    primedAudio.load();
    isAudioPrimed = true;
  } catch {
    // Could not create audio element - fail silently
    isAudioPrimed = false;
  }
}

// Play celebration sound using HTML5 Audio
export function playCelebrationSound() {
  try {
    // If we have a primed audio object, use it
    if (isAudioPrimed && primedAudio) {
      primedAudio.play().catch(() => {
        // Audio playback may fail due to autoplay restrictions
      });
      // Reset so next time we need to prime again
      isAudioPrimed = false;
      primedAudio = null;
      return;
    }

    // Fallback: try creating and playing a new audio element
    const soundUrl = getCelebrationSoundUrl();
    const audio = new Audio(soundUrl);
    audio.volume = 0.7;

    audio.play().catch(() => {
      // Audio playback may fail due to autoplay restrictions - this is expected
    });
  } catch {
    // Could not create audio element - fail silently
  }
}

interface CelebrationToastProps {
  t: string | number;
  title: string;
  description?: string;
  onClick?: () => void;
}

function CelebrationToast({ t, title, description, onClick }: CelebrationToastProps) {
  const handleClick = () => {
    if (onClick) {
      onClick();
      toast.dismiss(t);
    }
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    toast.dismiss(t);
  };

  return (
    <div
      onClick={handleClick}
      className="relative flex items-center gap-3 rounded-lg cursor-pointer transition-all duration-200 hover:translate-x-[-4px]"
      style={{
        width: '380px',
        padding: '16px',
        backgroundColor: 'var(--pm-toast-bg)',
        border: '1px solid var(--pm-cyan)',
        boxShadow: '0 0 20px var(--pm-cyan-glow), 0 10px 15px -3px rgba(0, 0, 0, 0.1)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = 'var(--pm-toast-bg-hover)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'var(--pm-toast-bg)';
      }}
    >
      {/* Icon with circular background */}
      <div
        className="flex-shrink-0 flex items-center justify-center rounded-full"
        style={{
          width: '32px',
          height: '32px',
          backgroundColor: 'var(--pm-toast-icon-bg)',
        }}
      >
        <PartyPopper style={{ width: '16px', height: '16px', color: 'var(--pm-cyan-text)' }} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p
          className="font-medium"
          style={{ color: 'var(--pm-text-primary)', fontSize: '14px' }}
        >
          {title}
        </p>
        {description && (
          <p
            style={{ color: 'var(--pm-text-muted)', fontSize: '13px', marginTop: '2px', opacity: 0.7 }}
          >
            {description}
          </p>
        )}
      </div>

      {/* Close button */}
      <button
        onClick={handleClose}
        className="flex-shrink-0 transition-opacity"
        style={{ color: 'var(--pm-text-muted)', opacity: 0.5 }}
        onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
        onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.5'; }}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

interface ShowCelebrationToastOptions {
  title: string;
  description?: string;
  onClick?: () => void;
  withConfetti?: boolean;
  withSound?: boolean;
}

export function showCelebrationToast({
  title,
  description,
  onClick,
  withConfetti = true,
  withSound = true,
}: ShowCelebrationToastOptions) {
  // Fire confetti and sound
  if (withConfetti) {
    triggerCelebration();
  }
  if (withSound) {
    playCelebrationSound();
  }

  // Show custom toast
  toast.custom(
    (t) => (
      <CelebrationToast
        t={t}
        title={title}
        description={description}
        onClick={onClick}
      />
    ),
    {
      duration: 8000,
      position: 'top-right',
    }
  );
}

export { CelebrationToast };
